import type { SupportedLocale } from "./datetime"

/**
 * Plain counts (requests, messages), the one place they are formatted
 * (NFR-DSH-08). Money goes through `money.ts`, dates through `datetime.ts`.
 */
export function formatCount(n: number, locale: SupportedLocale): string {
  return new Intl.NumberFormat(locale === "fr" ? "fr-FR" : "en-GB").format(n)
}

/** A ratio (0.92) as a percentage, at most `digits` decimals (one); `signed` for a change (+10 %). */
export function formatPercent(
  ratio: number,
  locale: SupportedLocale,
  { signed = false, digits = 1 }: { signed?: boolean; digits?: number } = {},
): string {
  return new Intl.NumberFormat(locale === "fr" ? "fr-FR" : "en-GB", {
    style: "percent",
    maximumFractionDigits: digits,
    signDisplay: signed ? "exceptZero" : "auto",
  }).format(ratio)
}

/** An i18next language ("fr-FR", "en") as one of the two the dashboard speaks. */
export const localeOf = (language: string): SupportedLocale =>
  language.startsWith("fr") ? "fr" : "en"

const UNITS = { en: ["B", "KB", "MB", "GB", "TB"], fr: ["o", "Ko", "Mo", "Go", "To"] } as const

/** Bytes in the largest binary unit below the value, at most one decimal (a phone's memory, storage). */
export function formatBytes(bytes: number, locale: SupportedLocale): string {
  const units = UNITS[locale]
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  const n = new Intl.NumberFormat(locale === "fr" ? "fr-FR" : "en-GB", {
    maximumFractionDigits: unit === 0 ? 0 : 1,
  }).format(value)
  return `${n} ${units[unit] ?? ""}`
}

const DAY_SYMBOL = { en: "d", fr: "j" } as const

/** A phone's uptime: "2 d 4 h", "3 h 7 min", "5 min". */
export function formatUptime(ms: number, locale: SupportedLocale): string {
  const minutes = Math.floor(ms / 60_000)
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  if (days > 0) return `${String(days)} ${DAY_SYMBOL[locale]} ${String(hours)} h`
  if (hours > 0) return `${String(hours)} h ${String(minutes % 60)} min`
  return `${String(minutes)} min`
}
